import React from "react";
import PropTypes from "prop-types";
import { ToastContainer } from "react-toastify";

import App from "./AppRouter";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import AnalyticsConsentBanner from "./components/AnalyticsConsentBanner";

const RootApp = ({
  initialReleasesPayload = null,
  initialShowPayload = null,
  initialMonthPayload = null,
  initialArchivePayload = null,
  initialBestOfPayload = null,
  initialMostRecommendedPayload = null,
  initialShowsLikePayload = null,
  initialPath = null,
  initialErrorStatus = null,
}) => {
  const { isDarkMode } = useTheme();

  return (
    <>
      <App
        initialReleasesPayload={initialReleasesPayload}
        initialShowPayload={initialShowPayload}
        initialMonthPayload={initialMonthPayload}
        initialArchivePayload={initialArchivePayload}
        initialBestOfPayload={initialBestOfPayload}
        initialMostRecommendedPayload={initialMostRecommendedPayload}
        initialShowsLikePayload={initialShowsLikePayload}
        initialPath={initialPath}
        initialErrorStatus={initialErrorStatus}
      />
      <ToastContainer position="bottom-right" theme={isDarkMode ? "dark" : "light"} autoClose={2500} />
      <AnalyticsConsentBanner />
    </>
  );
};

RootApp.propTypes = {
  initialReleasesPayload: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.object),
    referenceDay: PropTypes.string,
    lastUpdated: PropTypes.string,
    count: PropTypes.number,
    totalCount: PropTypes.number,
    candidateCount: PropTypes.number,
    stats: PropTypes.object,
    previousStats: PropTypes.object,
    previousLastUpdated: PropTypes.string,
    previousTotalCount: PropTypes.number,
    monthIndex: PropTypes.shape({
      months: PropTypes.arrayOf(PropTypes.object),
      count: PropTypes.number,
      lastUpdated: PropTypes.string,
    }),
    ifYouLikedPairings: PropTypes.arrayOf(PropTypes.object),
  }),
  initialShowPayload: PropTypes.shape({
    data: PropTypes.object,
    isArchived: PropTypes.bool,
    relatedShows: PropTypes.arrayOf(PropTypes.object),
    curationReasons: PropTypes.arrayOf(PropTypes.string),
    candidateCount: PropTypes.number,
  }),
  initialMonthPayload: PropTypes.shape({
    yearMonth: PropTypes.string,
    label: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.object),
    ifYouLikedPairings: PropTypes.arrayOf(PropTypes.object),
    count: PropTypes.number,
    previousYearMonth: PropTypes.string,
    nextYearMonth: PropTypes.string,
    totalCount: PropTypes.number,
    candidateCount: PropTypes.number,
    lastUpdated: PropTypes.string,
  }),
  initialArchivePayload: PropTypes.shape({
    months: PropTypes.arrayOf(
      PropTypes.shape({
        yearMonth: PropTypes.string,
        label: PropTypes.string,
        count: PropTypes.number,
        href: PropTypes.string,
        posters: PropTypes.arrayOf(
          PropTypes.shape({
            poster: PropTypes.string,
            title: PropTypes.string,
          })
        ),
      })
    ),
  }),
  initialBestOfPayload: PropTypes.shape({
    yearMonth: PropTypes.string,
    label: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.object),
    count: PropTypes.number,
    previousYearMonth: PropTypes.string,
    nextYearMonth: PropTypes.string,
    lastUpdated: PropTypes.string,
  }),
  initialMostRecommendedPayload: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.object),
    count: PropTypes.number,
    minInbound: PropTypes.number,
    lastUpdated: PropTypes.string,
  }),
  initialShowsLikePayload: PropTypes.shape({
    slug: PropTypes.string,
    anchor: PropTypes.object,
    picks: PropTypes.arrayOf(PropTypes.object),
    inboundContext: PropTypes.shape({
      count: PropTypes.number,
      sources: PropTypes.arrayOf(PropTypes.object),
    }),
    tasteTags: PropTypes.arrayOf(PropTypes.string),
    lastUpdated: PropTypes.string,
  }),
  initialPath: PropTypes.string,
  initialErrorStatus: PropTypes.oneOf([404, 410]),
};

export function buildAppTree(props = {}) {
  return (
    <React.StrictMode>
      <ThemeProvider>
        <RootApp {...props} />
      </ThemeProvider>
    </React.StrictMode>
  );
}

export default RootApp;
